<!--
  /oauth/callback: completes the PKCE exchange (bridge completeAuthorization), hands the token to the tab that
  opened the popup (BroadcastChannel + postMessage) and closes, or, in the full-redirect fallback, returns to the viewer.
-->
<script setup lang="ts">
import { onMounted, ref } from 'vue';
import Button from 'primevue/button';
import { announceToken, finishCallback, RETURN_KEY } from '@/open-in-nanome/oauth';

const emit = defineEmits<{ done: [] }>();
const status = ref<'working' | 'closing' | 'returning' | 'error'>('working');
const message = ref('Completing sign-in…');

onMounted(async () => {
  try {
    const token = await finishCallback();
    const popupFlow = !sessionStorage.getItem(RETURN_KEY) && (announceToken({ type: 'token', token }) || !!window.opener);
    if (popupFlow) {
      status.value = 'closing';
      message.value = 'Signed in. You can close this window.';
      setTimeout(() => window.close(), 300);
      return;
    }
    status.value = 'returning';
    message.value = 'Signed in. Returning to the viewer…';
    history.replaceState(null, '', '/');
    emit('done');
  }
  catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    announceToken({ type: 'error', message: msg });
    status.value = 'error';
    message.value = msg;
  }
});

function back() { history.replaceState(null, '', '/'); emit('done'); }
</script>

<template>
  <div class="h-full w-full flex flex-col items-center justify-center gap-4 p-6 text-center">
    <i :class="status === 'working' ? 'i-tabler-loader-2 animate-spin' : status === 'error' ? 'i-tabler-alert-triangle text-red' : 'i-tabler-check text-primary'" class="text-3xl" />
    <div>{{ message }}</div>
    <Button v-if="status !== 'working' && status !== 'closing'" label="Back to the viewer" rounded @click="back" />
  </div>
</template>
